import React, { useId } from 'react';
import { ChevronDown } from 'lucide-react';
import { resolveSelectPlaceholder } from '../utils/formPlaceholders';

const Select = ({
  label,
  name,
  value,
  onChange,
  options = [],
  error = '',
  required = false,
  disabled = false,
  placeholder,
  className = '',
  children,
  ...props
}) => {
  const fieldId = useId();
  const selectId = name || fieldId;
  const emptyOption = options.find((o) => String(o.value) === '');
  const skipEmptyDedup = placeholder === '';
  const filteredOptions = emptyOption && !skipEmptyDedup
    ? options.filter((o) => String(o.value) !== '')
    : options;

  const resolvedPlaceholder =
    placeholder !== undefined && placeholder !== null
      ? placeholder
      : emptyOption?.label
      ? emptyOption.label
      : resolveSelectPlaceholder({ label, name });

  return (
    <div className={`w-full ${className}`}>
      {label && (
        <label htmlFor={selectId} className="block text-sm font-bold text-gray-700 mb-2">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <div className="relative group">
        <select
          id={selectId}
          name={name}
          value={value}
          onChange={onChange}
          disabled={disabled}
          required={required}
          className={`w-full px-4 py-3 pr-12 border-2 rounded-xl font-medium transition-all duration-300 appearance-none
            ${error
              ? 'border-red-500 focus:border-red-600 focus:ring-4 focus:ring-red-500/20'
              : 'border-gray-200 hover:border-gray-300 focus:border-red-500 focus:ring-4 focus:ring-red-500/20'
            }
            ${disabled
              ? 'bg-gray-100 cursor-not-allowed text-gray-500'
              : !value && value !== 0
              ? 'bg-white text-gray-400'
              : 'bg-white text-gray-900'
            }
            focus:outline-none shadow-sm hover:shadow-md focus:shadow-lg cursor-pointer`}
          {...props}
        >
          {resolvedPlaceholder && (
            <option value="">{resolvedPlaceholder}</option>
          )}
          {filteredOptions.map((option, index) => (
            <option key={`${option.value}-${index}`} value={option.value}>
              {option.label}
            </option>
          ))}
          {children}
        </select>
        <div
          className={`absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none transition-colors ${
            error ? 'text-red-500' : 'text-gray-400 group-focus-within:text-red-500'
          }`}
        >
          <ChevronDown className="w-5 h-5" />
        </div>
      </div>
      {error && (
        <p className="mt-2 text-sm text-red-600 font-medium flex items-center gap-1">
          <span className="inline-block w-1 h-1 bg-red-600 rounded-full"></span>
          {error}
        </p>
      )}
    </div>
  );
};

export default Select;
